import {
  faAdd,
  faBracketsCurly,
  faPenToSquare,
  faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize, noop } from "lodash";

import { Button } from "src/base-components/Button";
import { DatasetSelector } from "src/datasets/DatasetSelector";
import { useDatasets } from "src/datasets/api/queries";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { SchemaOptions } from "src/router/SearchParams";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useAuthoringContext } from "src/router/routerContextHooks";

type Props = {
  type: SchemaOptions;
  handleAddField: () => void;
  isReadonly: boolean;
  onJsonSchemaClick: () => void;
  onManifestNodeBuilderClick?: () => void;
};

const SchemaEmptyStateActions: React.FC<Props> = ({
  type,
  isReadonly,
  handleAddField,
  onJsonSchemaClick,
  onManifestNodeBuilderClick,
}) => {
  const { workspace, flow } = useAuthoringContext();
  const datasets = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });
  const outputSchemaTypingInformation = false;
  const isManifestBuilderEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.manifestBuilder,
  );
  return (
    <div className="flex flex-col gap-y-2">
      <div className="w-60">
        <Tooltip
          title="This option is perfect for simple schemas with straightforward field types"
          asChild
        >
          <Button
            dataLoc="add-schema-field"
            disabled={isReadonly}
            iconLeft={faAdd}
            variant="secondary"
            fullWidth
            onClick={handleAddField}
          >
            Add field
          </Button>
        </Tooltip>
      </div>
      {isFeatureFlagEnabled(FEATURE_FLAGS.learnSchema) && (
        <>
          <p className="text-gray-500 font-inter-medium-12px">or</p>
          {type === SchemaOptions.Input ? (
            <DatasetSelector
              datasets={datasets.data || []}
              isLoading={datasets.isLoading}
              selectedDatasetId={null}
              onSelectData={noop}
            />
          ) : (
            <Tooltip
              disabled={outputSchemaTypingInformation}
              placement="bottom"
              title="Test run required to generate Output Schema fields"
            >
              <Button
                dataLoc="add-schema-field"
                disabled={isReadonly || !outputSchemaTypingInformation}
                iconLeft={faWandMagicSparkles}
                variant="secondary"
                fullWidth
              >
                Generate Output Schema
              </Button>
            </Tooltip>
          )}
        </>
      )}
      <p className="w-60 text-gray-500 font-inter-medium-12px">or</p>
      {isManifestBuilderEnabled && onManifestNodeBuilderClick && (
        <>
          <div className="w-60">
            <Tooltip
              title="Interactive schema builder with live UI preview"
              asChild
            >
              <Button
                dataLoc="manifest-node-builder-empty-button"
                disabled={isReadonly}
                iconLeft={faPenToSquare}
                variant="secondary"
                fullWidth
                onClick={() => onManifestNodeBuilderClick()}
              >
                Manifest Node Builder
              </Button>
            </Tooltip>
          </div>
          <p className="w-60 text-gray-500 font-inter-medium-12px">or</p>
        </>
      )}
      <div className="w-60">
        <Tooltip
          title="Create complex nested schemas from existing JSON schema"
          asChild
        >
          <Button
            dataLoc="schema-json-view-empty-button"
            disabled={isReadonly}
            variant="secondary"
            fullWidth
            onClick={() => onJsonSchemaClick()}
          >
            Generate from JSON schema
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export const SchemaEmptyState: React.FC<Props> = (props) => {
  const description = (() => {
    if (isFeatureFlagEnabled(FEATURE_FLAGS.learnSchema)) {
      return `No fields added for the ${capitalize(
        props.type,
      )} Schema yet. Click below to add a field or select a dataset to generate Schema`;
    }

    return ` Click below to start creating your ${props.type} schema by adding a field or generate it from JSON.`;
  })();

  return (
    <EmptyState
      action={<SchemaEmptyStateActions {...props} />}
      description={description}
      headline={`Create ${props.type} schema`}
      icon={faBracketsCurly}
    />
  );
};
